import type { CellContext, ColumnDef } from '@tanstack/react-table';
import type { TFunction } from 'i18next';
import type { JSX } from 'react';
import React from 'react';

import type { TAttributionModel } from '@feathr/blackbox';
import { DateTimeCell, NumberCell } from '@feathr/components';
import { TimeFormat } from '@feathr/hooks';

import type { IDailyStatsTotals, TDailyStatsRow } from './DailyStatsTable.utils';
import {
  getConversions,
  getConversionsColumnId,
  getCpa,
  getCpc,
  getCpm,
  getCtr,
} from './DailyStatsTable.utils';

interface IGetColumnsOptions {
  attributionModel: TAttributionModel;
  showConversions: boolean;
  showSpend: boolean;
  totals: IDailyStatsTotals;
}

/**
 * The conversions column is dynamic in the selected attribution model: its id, sort
 * value, cell, and footer total all key off `attributionModel`.
 */
export function getConversionsColumn(
  t: TFunction,
  attributionModel: TAttributionModel,
  total: number,
): ColumnDef<TDailyStatsRow> {
  return {
    id: getConversionsColumnId(attributionModel),
    accessorFn: (row): number => getConversions(row, attributionModel),
    header: t('Conversions'),
    meta: { align: 'right' },
    size: 200,
    // TanStack defaults number columns to descending-first; legacy react-table sorted
    // ascending-first for every column, so opt back into ascending-first.
    sortDescFirst: false,
    cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
      <NumberCell format={'count'} value={getConversions(row.original, attributionModel)} />
    ),
    footer: (): JSX.Element => <NumberCell format={'count'} value={total} />,
  };
}

/**
 * Column definitions for the daily stats table. `ctr`/`cpm`/`cpc`/`cpa` are derived
 * client-side and therefore non-sortable; the rest sort ascending-first. Footer totals
 * are grand totals over the full dataset, passed in via `totals`.
 */
export function getDailyStatsColumns(
  t: TFunction,
  { attributionModel, showConversions, showSpend, totals }: IGetColumnsOptions,
): Array<ColumnDef<TDailyStatsRow>> {
  const columns: Array<ColumnDef<TDailyStatsRow>> = [
    {
      id: 'date',
      accessorFn: (row): string | undefined => row.metadata?.date,
      header: t('Date'),
      size: 110,
      sortDescFirst: false,
      cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
        // Date-only calendar bucket — opt out of local conversion so the day doesn't shift.
        <DateTimeCell
          format={TimeFormat.shortDate}
          formatLocal={false}
          value={row.original.metadata?.date}
        />
      ),
    },
    {
      id: 'views',
      accessorFn: (row): number => row.num_views ?? 0,
      header: t('Views'),
      meta: { align: 'right' },
      sortDescFirst: false,
      cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
        <NumberCell format={'count'} value={row.original.num_views ?? 0} />
      ),
      footer: (): JSX.Element => <NumberCell format={'count'} value={totals.views} />,
    },
    {
      id: 'clicks',
      accessorFn: (row): number => row.num_clicks ?? 0,
      header: t('Clicks'),
      size: 110,
      meta: { align: 'right' },
      sortDescFirst: false,
      cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
        <NumberCell format={'count'} value={row.original.num_clicks ?? 0} />
      ),
      footer: (): JSX.Element => <NumberCell format={'count'} value={totals.clicks} />,
    },
    {
      id: 'ctr',
      header: t('CTR'),
      size: 65,
      enableSorting: false,
      meta: { align: 'right' },
      cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
        <NumberCell format={'percent'} value={getCtr(row.original)} />
      ),
    },
    {
      id: 'num_users_active',
      accessorFn: (row): number => row.num_users_active ?? 0,
      header: t('Active Audience'),
      size: 150,
      meta: { align: 'right' },
      sortDescFirst: false,
      cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
        <NumberCell format={'count'} value={row.original.num_users_active ?? 0} />
      ),
    },
  ];

  if (showSpend) {
    columns.push(
      {
        id: 'spend',
        accessorFn: (row): number => row.spend ?? 0,
        header: t('Spend'),
        size: 140,
        meta: { align: 'right' },
        sortDescFirst: false,
        cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
          <NumberCell format={'currency'} value={row.original.spend ?? 0} />
        ),
        footer: (): JSX.Element => <NumberCell format={'currency'} value={totals.spend} />,
      },
      {
        id: 'cpm',
        header: t('CPM'),
        size: 65,
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
          <NumberCell format={'currency'} value={getCpm(row.original)} />
        ),
      },
      {
        id: 'cpc',
        header: t('CPC'),
        size: 65,
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
          <NumberCell format={'currency'} value={getCpc(row.original)} />
        ),
      },
      {
        id: 'cpa',
        header: t('CPA'),
        size: 65,
        enableSorting: false,
        meta: { align: 'right' },
        cell: ({ row }: CellContext<TDailyStatsRow, unknown>): JSX.Element => (
          <NumberCell format={'currency'} value={getCpa(row.original)} />
        ),
      },
    );
  }

  if (showConversions) {
    columns.push(getConversionsColumn(t, attributionModel, totals.conversions));
  }

  return columns;
}
